// src/encounter/structured-note/label-parts-model.ts
//
// Section 4 · the visit note labels, the model of `label-parts.tsx` (Antonin's catalog @f28d00c1, ClinicalFields `splitLabel`
// and EncounterDev `PartTitleCtx`, 2 Nov), behind `encounterLatest` (`?x=encounter-latest`).
import { createContext, useState } from 'react';
import { isEncounterFlagOn } from '@/shared/lib/encounter-flags';

/** The title of the group a line sits in, so a label that only repeats it is not drawn twice. */
export const NoteGroupTitleContext = createContext('');

/** Same words, ignoring case and spacing. */
export const sameLabel = (a: string, b: string) => a.replace(/\s+/g, ' ').trim().toLowerCase() === b.replace(/\s+/g, ' ').trim().toLowerCase();

/** "Gait / Transferring (mark one response)" → ["Gait / Transferring", "mark one response"]; no qualifier → [text, null]. */
export function splitLabel(text: string): [string, string | null] {
  const paren = /^(.+?)\s*\(([^()]+)\)\s*$/.exec(text);
  if (paren && paren[1].trim().length > 1) return [paren[1].trim(), paren[2].trim()];
  // an em dash only: a spaced en dash is a range ("Pain scale 0 – 10"), not a qualifier
  const dash = /^(.+?)\s+—\s+(.+)$/.exec(text);
  if (dash && dash[1].trim().length > 1) return [dash[1].trim(), dash[2].trim()];
  return [text, null];
}

/** List A (`visitNoteV2`, PROD-3395, every template): the label the line shows. The text before the FIRST ': ' (a colon then a
 *  space) when there is text on both sides ("Symptoms: onset, severity …" → "Symptoms"); never cut at a comma, nor at a colon
 *  with no space after it (a ratio or a time: "I:E ratio", "1:2"). No such colon → the text unchanged. The full text is the
 *  second value whenever the shown label differs, for the info tooltip; display only, the item is unchanged. */
export function shortLabel(text: string): [string, string | null] {
  const cut = /:\s/.exec(text);
  if (!cut) return [text, null];
  const head = text.slice(0, cut.index).trim();
  if (!head || !text.slice(cut.index + 1).trim()) return [text, null];
  return [head, text];
}

/** The group heading the note draws: its title, or '' when it has none, is untitled, or repeats the section's or the block's
 *  title (`repeats`, structured-note-section.tsx `Narrative`). A line label is hidden only when it repeats a heading on screen. */
export function shownGroupTitle(title: string | undefined, untitled: boolean | undefined, repeats: readonly string[]): string {
  if (!title || untitled || repeats.some((r) => sameLabel(r, title))) return '';
  return title;
}

/** True only in a browser on the catalog's encounter note (`visitNoteV2`, list A). Read once per mount, like the frame. */
export function useVisitNoteV2(): boolean {
  const [on] = useState(() => isEncounterFlagOn('visitNoteV2'));
  return on;
}

/** True only in a browser on the Section 4 look. Read once per mount, like the frame. */
export function useEncounterLatest(): boolean {
  const [on] = useState(() => isEncounterFlagOn('encounterLatest'));
  return on;
}
